import { isEmpty } from 'lodash';
import React from 'react';
import { LuArrowDownLeft, LuArrowUpRight, LuPiggyBank } from 'react-icons/lu';

import { FrequencyType } from '@ndvr/portfolio-api';

import { BaseCard } from '@/components/BaseCard';
import { formatCurrency, formatDate } from 'utils';

import { frequencyTypeToLabel } from '../../utils/frequencyToLabel';
import { useLiquidityPlanningData } from '../liquidity-planning/useLiquidityPlanningData';

interface LiquidityPlanningCardProps {
    accountGroupGUID: string;
}

export const LiquidityPlanningCard = ({ accountGroupGUID }: LiquidityPlanningCardProps) => {
    const { isLoading, withdrawals, contributions } = useLiquidityPlanningData(accountGroupGUID);

    return (
        <BaseCard
            title="Liquidity Planning"
            icon={LuPiggyBank}
            loading={isLoading}
            headerClassName="bg-[#C6E6E6]"
            navigateUrl="liquidity-planning"
        >
            <div className="mb-4 text-sm text-gray-600">
                {withdrawals?.length || 0} Withdrawals • {contributions?.length || 0} Contributions
            </div>

            {/* All Cashflows in Single Column */}
            {!isEmpty(withdrawals) && (
                <div className="space-y-3">
                    {/* Withdrawals */}
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <LuArrowDownLeft className="h-4 w-4" />
                        Withdrawals
                    </div>
                    {withdrawals?.map((cashFlow) => (
                        <div key={cashFlow.id} className="border-border relative rounded-lg border bg-gray-50 p-3">
                            <div className="pr-16 text-sm font-medium text-gray-800">{cashFlow.title}</div>
                            <div className="mt-1 text-xs text-gray-700">
                                {formatCurrency(cashFlow.amount)} •{' '}
                                {frequencyTypeToLabel(cashFlow.frequency as FrequencyType)} •{' '}
                                {formatDate(cashFlow.startDate)} {cashFlow.endDate ? 'to' : ''}{' '}
                                {formatDate(cashFlow.endDate)}
                                {cashFlow.occurrenceCount && (
                                    <span className="ml-1 text-xs text-gray-500">
                                        ({cashFlow.occurrenceCount} occurrences)
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {!isEmpty(contributions) && (
                <div className="space-y-3">
                    {/* Contributions */}
                    <div className="mt-6 flex items-center gap-2 text-sm font-medium text-gray-700">
                        <LuArrowUpRight className="h-4 w-4" />
                        Contributions
                    </div>
                    {contributions?.map((cashFlow) => (
                        <div key={cashFlow.id} className="border-border relative rounded-lg border bg-gray-50 p-3">
                            <div className="pr-16 text-sm font-medium text-gray-800">{cashFlow.title}</div>
                            <div className="mt-1 text-xs text-gray-700">
                                {formatCurrency(cashFlow.amount)} •{' '}
                                {frequencyTypeToLabel(cashFlow.frequency as FrequencyType)} •{' '}
                                {formatDate(cashFlow.startDate)} {cashFlow.endDate ? 'to' : ''}{' '}
                                {formatDate(cashFlow.endDate)}
                                {cashFlow.occurrenceCount && (
                                    <span className="ml-1 text-xs text-gray-500">
                                        ({cashFlow.occurrenceCount} occurrences)
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </BaseCard>
    );
};
